import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  DatePicker,
  Select,
  Space,
  Tag,
  Modal,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { getExpenseList } from "../api/host";
import ExpenseDrawer from "./ExpenseDrawer";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export default function ExpenseList() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editData, setEditData] = useState(null);

  const [filters, setFilters] = useState({
    dateRange: null,
    type: undefined,
    partner: undefined,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getExpenseList({
        type: filters.type,
        partner: filters.partner,
        expenseStartDate: filters.dateRange?.[0]?.format("YYYY-MM-DD"),
        expenseEndDate: filters.dateRange?.[1]?.format("YYYY-MM-DD"),
      });
      setData(res.data.list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  /**
   * 金额格式化
   */
  const formatAmount = (val) => {
    if (!val) return "0";
    return Number(val).toFixed(2);
  };

  /**
   * 房源分摊表
   */
  const RoomTable = ({ data }) => {
    if (!data?.length) return <Text type="secondary">无房源分摊</Text>;

    return (
      <Table
        size="small"
        pagination={false}
        rowKey={(r, i) => i}
        columns={[
          {
            title: "房间",
            dataIndex: ["host", "name"],
            key: "host",
          },
          {
            title: "金额",
            dataIndex: "amount",
            render: (v) => formatAmount(v),
          },
          {
            title: "开始",
            dataIndex: "startDate",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
          },
          {
            title: "结束",
            dataIndex: "endDate",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
          },
        ]}
        dataSource={data}
        bordered
      />
    );
  };

  /**
   * 资产分摊表
   */
  const AssetTable = ({ data }) => {
    if (!data?.length) return <Text type="secondary">无资产分摊</Text>;

    return (
      <Table
        size="small"
        pagination={false}
        rowKey={(r, i) => i}
        columns={[
          {
            title: "资产",
            dataIndex: ["asset", "name"],
            key: "asset",
          },
          {
            title: "金额",
            dataIndex: "amount",
            render: (v) => formatAmount(v),
          },
          {
            title: "内容",
            dataIndex: "content",
          },
        ]}
        dataSource={data}
        bordered
      />
    );
  };

  /**
   * Expand Render
   */
  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: 12 }}>
        <div style={{ marginBottom: 12 }}>
          <Text strong>房源分摊</Text>
          <RoomTable data={record.roomAllocations} />
        </div>

        <div>
          <Text strong>资产分摊</Text>
          <AssetTable data={record.assetAllocations} />
        </div>
      </div>
    );
  };

  /**
   * 主表 columns
   */
  const columns = [
    {
      title: "支付日期",
      dataIndex: "expenseDate",
      render: (v) => dayjs(v).format("YYYY-MM-DD"),
      sorter: true,
    },
    {
      title: "业务日期",
      dataIndex: "businessDate",
      render: (v) => dayjs(v).format("YYYY-MM-DD"),
    },
    {
      title: "类型",
      dataIndex: "type",
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: "名称",
      dataIndex: "name",
    },
    {
      title: "金额",
      dataIndex: "amount",
      render: (v) => <Text strong>¥ {formatAmount(v)}</Text>,
    },
    {
      title: "付款对象",
      dataIndex: ["partner", "name"],
    },
    {
      title: "付款方式",
      dataIndex: "paymentMethod",
    },
    {
      title: "票据号",
      dataIndex: "invoiceNo",
    },
    {
      title: "状态",
      dataIndex: "status",
      render: (v) =>
        v === "NORMAL" ? (
          <Tag color="green">正常</Tag>
        ) : (
          <Tag color="red">作废</Tag>
        ),
    },
  ];

  /**
   * 汇总
   */
  const tableSummary = (pageData) => {
    let total = 0;

    pageData.forEach((item) => {
      total += Number(item.amount || 0);
    });

    return (
      <Table.Summary>
        <Table.Summary.Row>
          <Table.Summary.Cell colSpan={4}>
            <Text strong>本页合计</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            <Text strong>¥ {formatAmount(total)}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell colSpan={4} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const recordSubmit = () => {
    return;
  };
  return (
    <div style={{ padding: 16 }}>
      {/* ================= 顶部筛选 ================= */}
      <Space style={{ marginBottom: 12 }} wrap>
        <RangePicker
          onChange={(v) =>
            setFilters((f) => ({
              ...f,
              dateRange: v,
            }))
          }
        />

        <Select
          placeholder="费用类型"
          allowClear
          style={{ width: 140 }}
          onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
        >
          <Select.Option value="RENT">房租</Select.Option>
          <Select.Option value="PROPERTY">物业费</Select.Option>
          <Select.Option value="REPAIR">维修</Select.Option>
        </Select>

        <Button type="primary" onClick={() => setOpenDrawer(true)}>
          增加支出
        </Button>

        <Button>导出</Button>
      </Space>
      <ExpenseDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onSuccess={recordSubmit}
        editData={editData}
      />
      {/* ================= 表格 ================= */}
      <Table
        rowKey="_id"
        loading={loading}
        dataSource={data}
        columns={columns}
        expandable={{
          expandedRowRender,
        }}
        pagination={{
          pageSize: 10,
        }}
        summary={tableSummary}
      />
    </div>
  );
}
